import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const getStorageKey = (workspaceId) => `mz_onboarding_v1:${workspaceId || 'global'}`;

export default function OnboardingChecklist({ token, workspace_id, hasTimers }) {
  useTranslation();
  const storageKey = getStorageKey(workspace_id);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(storageKey) === 'dismissed'
  );
  const [hasProjects, setHasProjects] = useState(false);
  const [hasTeamMembers, setHasTeamMembers] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(storageKey) === 'dismissed');
  }, [storageKey]);

  useEffect(() => {
    if (dismissed) return;
    const cfg = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
    const body = JSON.stringify({ workspace_id });
    Promise.all([
      axios.post('/api/project/list', body, cfg),
      axios.post('/api/team/list', JSON.stringify({ workspace_id, is_active: 1 }), cfg),
    ])
      .then(([projRes, teamRes]) => {
        setHasProjects(projRes.data.results && projRes.data.results.length > 0);
        setHasTeamMembers(teamRes.data.results && teamRes.data.results.length > 1);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [token, workspace_id, dismissed]);

  const dismiss = () => {
    localStorage.setItem(storageKey, 'dismissed');
    setDismissed(true);
  };

  const startTour = () => {
    window.dispatchEvent(new Event('minutezilla:start-product-tour'));
  };

  // Auto-dismiss 2 s after all steps complete
  useEffect(() => {
    if (loaded && hasTimers && hasProjects && hasTeamMembers) {
      const t = setTimeout(dismiss, 2000);
      return () => clearTimeout(t);
    }
  }, [loaded, hasTimers, hasProjects, hasTeamMembers]);

  if (dismissed || !loaded) return null;

  const steps = [
    {
      done: hasTimers,
      label: 'Track your first timer',
      sub: 'Hit the play button above to record your first time entry.',
      action: null,
    },
    {
      done: hasProjects,
      label: 'Create a project',
      sub: 'Organise your time entries by project and client.',
      action: <Link to="/projects" className="onboarding-checklist__step-action">{tr("Go to Projects →")}</Link>,
    },
    {
      done: hasTeamMembers,
      label: 'Invite a team member',
      sub: 'Grow your workspace by inviting colleagues.',
      action: <Link to="/team" className="onboarding-checklist__step-action">{tr("Go to Team →")}</Link>,
    },
  ];

  const completedCount = steps.filter(s => s.done).length;

  return (
    <div className="onboarding-checklist">
      <div className="onboarding-checklist__header">
        <div>
          <div className="onboarding-checklist__title">{tr("Get started with Minutezilla")}</div>
          <div className="onboarding-checklist__progress-text">{completedCount} {tr("of")} {steps.length} {tr("complete")}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            className="onboarding-checklist__dismiss"
            onClick={startTour}
            title={tr("Start product tour")}
            style={{ fontSize: '12px', padding: '0 10px', width: 'auto' }}
          >{tr("Tour")} </button>
          <button className="onboarding-checklist__dismiss" onClick={dismiss} title={tr("Dismiss")}>✕</button>
        </div>
      </div>

      <div className="onboarding-checklist__progress-bar">
        <div
          className="onboarding-checklist__progress-fill"
          style={{ width: `${(completedCount / steps.length) * 100}%` }}
        />
      </div>

      <ul className="onboarding-checklist__steps">
        {steps.map((step, i) => (
          <li
            key={i}
            className={`onboarding-checklist__step${step.done ? ' onboarding-checklist__step--done' : ''}`}
          >
            <span className="onboarding-checklist__check">
              {step.done ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="10" fill="#10b981" />
                  <path d="M6 10l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <span className="onboarding-checklist__check-empty">{i + 1}</span>
              )}
            </span>
            <div className="onboarding-checklist__step-body">
              <div className="onboarding-checklist__step-label">{tr(step.label)}</div>
              {!step.done && (
                <>
                  <div className="onboarding-checklist__step-sub">{tr(step.sub)}</div>
                  {step.action}
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
